import { ISSUE_ATTACHMENTS_FOLDER_NAME } from "../constants/systemFolders";

/**
 * The project's Brief folder: the database refuses to rename, move, hide or
 * delete it, and the organiser leaves it alone, so no surface offers those.
 * Files and subfolders inside it are ordinary.
 */
export function isBriefFolder(folder: { variant?: string } | null | undefined): boolean {
  return folder?.variant === "brief";
}

/**
 * Marks the project's Brief folder in a built tree — the one tagging pass every
 * folder surface shares, so it has one icon, one on-screen name and one set of
 * refused actions everywhere. Returns the tree untouched when there is none.
 */
export function tagBriefFolder<T extends { id: string; variant?: string; children: T[] }>(
  folders: T[],
  briefFolderId: string | null,
  displayName: string
): T[] {
  if (briefFolderId === null) return folders;
  const tag = (folder: T): T => ({
    ...folder,
    children: folder.children.map(tag),
    ...(String(folder.id) === briefFolderId ? { variant: "brief" as const, displayName } : {}),
  });
  return folders.map(tag);
}

/**
 * The project's Issue Attachments folder, as tagged by `tagIssueAttachmentsFolder`.
 * Read-only rules key off the stored name (`isSystemFolder`); this is only how
 * it is drawn and named.
 */
export function isIssueAttachmentsFolder(folder: { variant?: string } | null | undefined): boolean {
  return folder?.variant === "issue-attachments";
}

/**
 * Marks every Issue Attachments folder in a built tree with its own icon and its
 * name in the UI language. Known by its stored name, which the database reserves
 * per project, so no lookup is needed. The `variant` already set on a folder wins.
 */
export function tagIssueAttachmentsFolder<
  T extends { name: string; variant?: string; children: T[] },
>(folders: T[], displayName: string): T[] {
  const tag = (folder: T): T => ({
    ...folder,
    children: folder.children.map(tag),
    ...(folder.variant === undefined && folder.name === ISSUE_ATTACHMENTS_FOLDER_NAME
      ? { variant: "issue-attachments" as const, displayName }
      : {}),
  });
  return folders.map(tag);
}

/** What a folder is called on screen — the Brief and Issue Attachments names follow the UI language. */
export function folderLabel(folder: { name: string; displayName?: string }): string {
  return folder.displayName ?? folder.name;
}
